import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Train, Heart, Brain, Scale, Users } from 'lucide-react';
import CTA from '../../components/CTA';

const NewtownabbeyWeightManagement: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Weight Management Hypnotherapy Newtownabbey | Family Wellness</title>
        <meta
          name="description"
          content="Weight management hypnotherapy serving Newtownabbey from Belfast clinic (just 6 miles). Address parenting stress eating, family habits, emotional eating. CNHC registered."
        />
        <meta name="keywords" content="weight loss hypnotherapy Newtownabbey, weight management Newtownabbey, parenting stress eating Rathcoole, emotional eating Glengormley, mindful eating Newtownabbey" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/newtownabbey-weight-management/" />
        <meta property="og:title" content="Weight Management Hypnotherapy Newtownabbey | Family Wellness" />
        <meta property="og:description" content="Weight management hypnotherapy serving Newtownabbey. Address parenting stress eating, family habits, sustainable family wellness." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Weight Management Newtownabbey",
            "description": "CNHC registered weight management hypnotherapy serving Newtownabbey. Specializing in parenting stress eating, family wellness, emotional eating patterns.",
            "url": "https://www.sharonclare-hypnotherapy.com/newtownabbey-weight-management/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Weight Management Hypnotherapy",
            "areaServed": {
              "@type": "City",
              "name": "Newtownabbey",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Weight Management Hypnotherapy Serving Newtownabbey
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Address parenting stress eating and build healthy family habits
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Just 6 miles</span> from Newtownabbey via M5/M2 • <span className="font-semibold">10-15 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Glider bus service available • Online sessions for maximum convenience</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Weight Management Hypnotherapy for Newtownabbey Families
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                I serve the Newtownabbey community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, just 6 miles south via the M5 and M2 motorways. The journey takes only 10-15 minutes by car, making professional weight management support exceptionally convenient for Newtownabbey residents. As the closest major town to my Belfast clinic, you're ideally positioned for regular appointments without significant travel time.
              </p>
              <p>
                Parenting stress and weight gain often go hand in hand. The pressure of managing work, childcare, school runs, homework, extracurricular activities, household responsibilities, and trying to maintain some semblance of self-care leaves many parents emotionally and physically exhausted. When you're running on empty, food becomes a quick source of comfort and energy - snacking on the kids' leftovers, eating their snacks while packing lunchboxes, stress eating after finally getting everyone to bed, or using the biscuit tin as an emotional crutch during overwhelming days.
              </p>
              <p>
                This isn't about willpower or discipline - it's about your nervous system being in constant stress mode. When you're juggling multiple demands, sleep-deprived, and putting everyone else's needs before your own, your body's stress response stays activated. This increases hunger hormones, intensifies cravings for high-calorie comfort foods, and makes it nearly impossible to resist eating for emotional relief. The weight gain feels frustrating because you know what you should be doing, but your exhausted, stressed nervous system overrides your best intentions.
              </p>
              <p>
                Hypnotherapy addresses parenting stress eating at its root - not by adding another item to your already overwhelming to-do list, but by teaching your nervous system to switch off the stress response. When you're calmer, cravings naturally reduce. When you have healthier ways to manage stress, food loses its power as an emotional crutch. And when you model healthier eating habits, you're also setting your children up for better relationships with food. For busy Newtownabbey parents, I also offer online sessions - ideal for fitting in during school hours or after bedtime without needing to arrange childcare or travel.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Parenting Stress Eating Patterns */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Common Parenting Stress Eating Patterns
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <Users className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Snacking on Children's Food
                </h3>
                <p className="text-gray-700">
                  Finishing your children's meals, snacking on their lunchbox treats, eating the crisps and biscuits while making packed lunches - these mindless calories add up quickly. You're not even hungry, but the food is there and eating becomes automatic. Hypnotherapy helps you break these automatic patterns, becoming more mindful about when you're genuinely hungry versus eating out of habit or opportunity.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Clock className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Evening Stress Eating After Bedtime
                </h3>
                <p className="text-gray-700">
                  After the chaos of dinner, bath time, and bedtime battles, many parents reward themselves with food and drink - the biscuit tin, crisps, chocolate, wine. It feels like your only "me time," but this nightly pattern can easily add 500+ calories you don't need. Hypnotherapy helps you find healthier ways to unwind and reward yourself without undoing the day's good choices.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Heart className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Emotional Eating Under Family Pressure
                </h3>
                <p className="text-gray-700">
                  When you're dealing with tantrums, sibling arguments, parenting disagreements with your partner, financial stress, or the overwhelm of managing everything, food becomes a quick emotional release. You're not eating because you're hungry - you're eating to cope with difficult emotions you don't have time to process properly. Addressing the emotional patterns is key to breaking this cycle.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Brain className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Exhaustion Eating for Energy
                </h3>
                <p className="text-gray-700">
                  Sleep deprivation and chronic exhaustion drive cravings for quick energy - sugary snacks, carbohydrates, caffeine. When you're running on fumes, your body seeks rapid energy sources. The problem is this creates a blood sugar roller coaster that makes you even more tired and craving more sugar. Hypnotherapy helps improve sleep quality and reduces the exhaustion-driven eating cycle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modeling Healthy Habits */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Modeling Healthy Eating Habits for Your Family
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                One powerful motivation for addressing your own weight struggles is the impact on your children. Children learn eating behaviors primarily from observing their parents, not from what we tell them. If they see you stress eating, using food for emotional comfort, or constantly dieting and expressing dissatisfaction with your body, they learn to do the same.
              </p>
              <p>
                By addressing your own emotional eating patterns through hypnotherapy, you're not just helping yourself - you're breaking intergenerational patterns and modeling healthier relationships with food for your children. When you learn to manage stress without automatically reaching for food, your children observe and internalize healthier coping strategies. When you eat mindfully rather than mindlessly snacking, you demonstrate what healthy eating actually looks like. When you speak kindly about your body rather than constantly criticizing it, you teach body acceptance.
              </p>
              <p>
                Many Newtownabbey parents find this motivation particularly powerful. You want your children to grow up with healthy relationships with food, free from the weight struggles and emotional eating patterns that you've experienced. By doing this work for yourself, you're also creating a healthier family food environment that benefits everyone.
              </p>
              <p>
                Hypnotherapy helps you make these changes without adding more pressure to your already full plate. Rather than adding another rigid diet or time-consuming exercise program, we work on the underlying stress and emotional patterns. The changes feel natural rather than forced, and sustainable rather than exhausting. Your family benefits from a calmer, more present parent who isn't constantly stressed about food and weight.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Newtownabbey Parents Say About Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Between work, two kids, and running a household, I was stress eating constantly. Sharon helped me understand I was eating to cope with overwhelm, not because I was hungry. After 5 sessions, I've lost over a stone and I'm modeling much healthier eating habits for my kids. The short drive from Glengormley was so convenient."
                </p>
                <p className="font-semibold text-gray-900">K.T., Glengormley - Parenting Stress Eating</p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The online sessions were a lifesaver - I could fit them in during school hours without arranging childcare. Sharon's approach addressed the evening stress eating that was my biggest problem. I've lost 1.5 stone over 6 months and my relationship with food has completely changed. I'm also setting a better example for my daughters."
                </p>
                <p className="font-semibold text-gray-900">R.M., Rathcoole - Evening Stress Eating</p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I didn't realize how much I was snacking on my kids' food until Sharon pointed out the pattern. Breaking those automatic habits has made such a difference. I've lost a stone in 4 months, but more importantly, I'm teaching my children mindful eating by example. The convenience from Monkstown (10 minutes) made regular appointments realistic."
                </p>
                <p className="font-semibold text-gray-900">A.D., Monkstown - Family Wellness</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Newtownabbey Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with parenting stress eating?
                </h3>
                <p className="text-gray-700">
                  Yes, parenting stress eating is one of the most common patterns I address. When you're juggling work, childcare, household responsibilities, and trying to maintain some self-care, your nervous system stays in constant stress mode. This drives emotional eating, exhaustion eating, and the evening stress eating many parents experience after finally getting kids to bed. Hypnotherapy works by teaching your nervous system to switch off the stress response, which naturally reduces emotional eating urges. We also work on finding healthier coping mechanisms that don't involve food. Most parents notice significant changes within 4-6 sessions.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How can I stop snacking on my children's food?
                </h3>
                <p className="text-gray-700">
                  Snacking on children's food - finishing their meals, eating their lunchbox snacks, nibbling while preparing their food - is an automatic habit driven by opportunity and mindlessness rather than hunger. Hypnotherapy breaks these automatic patterns by increasing your awareness and helping you reconnect with genuine hunger signals. You'll find yourself naturally pausing before automatically eating food that's in front of you, asking yourself if you're actually hungry, and making conscious choices rather than eating on autopilot. The changes feel natural rather than requiring constant vigilance.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I fit hypnotherapy into a busy family schedule?
                </h3>
                <p className="text-gray-700">
                  Absolutely. The ultra-convenient location from Newtownabbey (just 6 miles, 10-15 minutes) makes in-person sessions very manageable. Many parents schedule appointments during school hours or on weekends. Online sessions offer even more flexibility - you can attend from home during school hours, work-from-home breaks, or even in the evening after kids are in bed. Sessions are 60-90 minutes, and you'll also use daily audio recordings (10 minutes) which you can listen to while commuting, doing housework, or before sleep. The time investment is minimal compared to the impact on your wellbeing and your family.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Will addressing my eating habits benefit my children?
                </h3>
                <p className="text-gray-700">
                  Yes, significantly. Children learn eating behaviors primarily from observing their parents, not from what we tell them. When you model mindful eating, manage stress without automatically reaching for food, and speak kindly about your body, your children internalize these healthier patterns. Many parents find this motivation powerful - breaking intergenerational patterns of emotional eating and weight struggles so their children grow up with healthier relationships with food. By doing this work for yourself, you're also creating a healthier family food environment that benefits everyone.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How do I stop evening stress eating after the kids are in bed?
                </h3>
                <p className="text-gray-700">
                  Evening stress eating - rewarding yourself with food and drink after the chaos of dinner, bath, and bedtime - is extremely common among parents. It feels like your only "me time" and a well-deserved reward for surviving the day. The problem is this nightly pattern can easily add hundreds of calories you don't need. Hypnotherapy addresses this by reducing your overall stress response (so you're not as desperate for stress relief by evening), building healthier evening routines, and helping you find alternative ways to unwind that don't involve food. We also work on improving sleep quality, which dramatically reduces evening cravings.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is online hypnotherapy as effective as in-person?
                </h3>
                <p className="text-gray-700">
                  Yes, online hypnotherapy sessions via video call are just as effective as in-person sessions. The therapeutic process works equally well through a screen. For busy Newtownabbey parents, online sessions often work better because they eliminate travel time, don't require arranging childcare, and can be scheduled more flexibly. You need a quiet, private space and stable internet, but beyond that, the experience is identical. Many parents actually prefer online sessions because they're in their own comfortable space, which helps them relax more deeply during hypnotherapy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default NewtownabbeyWeightManagement;
